import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../i18n';
import { useOrder } from '../OrderContext';
import Header from '../components/Header';
import { AlcoholBadge, DescriptionInfo } from '../components/ProductInfo';
import { fetchMachineProducts, isMixedProduct, isOnPipe, type MachineProduct } from '../api';
import { useHoldRepeat } from '../hooks/useHoldRepeat';

const MAX_ML = 200;
const MIN_ML = 40;
const STEP_ML = 10;

const snapToBuckets = (raw: number): number => {
  if (!Number.isFinite(raw) || raw <= 0) return 0;
  if (raw < MIN_ML / 2) return 0;
  const clamped = Math.max(MIN_ML, Math.min(MAX_ML, raw));
  return Math.round(clamped / STEP_ML) * STEP_ML;
};

/** The most ml this drink can ever hold in one mix: the 200ml mix budget,
 *  capped by its live backend stock and by its machine-configured
 *  max_pour_ml limit (omitted = no limit) — both snapped DOWN to the step
 *  grid so any allowed value is actually dispensable. */
const capFor = (p: MachineProduct): number => {
  const stockCap = Math.floor(p.quantity_ml / STEP_ML) * STEP_ML;
  const pourCap = p.max_pour_ml != null ? Math.floor(p.max_pour_ml / STEP_ML) * STEP_ML : MAX_ML;
  return Math.min(MAX_ML, stockCap, pourCap);
};

const ProductPlaceholder = ({ name }: { name: string }) => {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
  return (
    <div className="flex h-full w-full items-center justify-center rounded-full bg-[rgba(255,255,255,0.08)] font-display font-bold text-[var(--muted-lt)]" style={{ fontSize: 'clamp(1rem,3.5vh,1.8rem)' }}>
      {initials}
    </div>
  );
};

const DrinkIcon = ({ product }: { product: MachineProduct }) =>
  product.image_url ? (
    <img src={product.image_url} alt="" className="block h-full w-full object-contain" />
  ) : (
    <ProductPlaceholder name={product.name} />
  );

const CustomMixPage = () => {
  const navigate = useNavigate();
  const { resetOrder, updateOrder, order } = useOrder();
  const [products, setProducts] = useState<MachineProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mix, setMix] = useState<Record<string, number>>({});

  useEffect(() => {
    fetchMachineProducts()
      .then((all) => {
        // Custom Mix ingredients are ONLY the 'standalone' base liquids that
        // are assigned to a physical pipe — 'mixed' presets live on the Main
        // Menu, and an unassigned liquid can't be dispensed. A drink also
        // needs a usable cap of at least MIN_ML (from live stock and/or the
        // machine's max_pour_ml limit): below that it can never be included
        // (an included drink is always ≥ MIN_ML), so it is hidden rather than
        // shown dead. Listed in pipe order (S1…S7), like the machine panel.
        const pourable = all
          .filter(
            (p) => !isMixedProduct(p) && p.is_enabled && isOnPipe(p) && capFor(p) >= MIN_ML,
          )
          .sort(
            (a, b) => (a.pipe_number ?? 99) - (b.pipe_number ?? 99) || a.name.localeCompare(b.name),
          );
        setProducts(pourable);
        setMix(Object.fromEntries(pourable.map((p) => [p.product_id, 0])));
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
  }, []);

  const totalMl = Object.values(mix).reduce((s, v) => s + v, 0);
  const remaining = MAX_ML - totalMl;

  const setAmount = (product: MachineProduct, value: number) => {
    const id = product.product_id;
    const bucketed = snapToBuckets(value);
    if (bucketed === 0) {
      setMix((prev) => ({ ...prev, [id]: 0 }));
      return;
    }
    // A drink can grow up to the smaller of: the room left in the total
    // budget, and its own cap (stock and/or max_pour_ml).
    const room = Math.min((mix[id] ?? 0) + remaining, capFor(product));
    if (room < MIN_ML) return;
    const capped = Math.min(bucketed, room);
    const final = capped < MIN_ML ? 0 : Math.round(capped / STEP_ML) * STEP_ML;
    setMix((prev) => ({ ...prev, [id]: final }));
  };

  const totalPrice = products.reduce(
    (sum, p) => sum + ((mix[p.product_id] ?? 0) / MAX_ML) * ((p.price_cents ?? 0) / 100),
    0,
  );

  const handleConfirm = () => {
    const user = order.user;
    const nonZeroMix = Object.fromEntries(
      Object.entries(mix).filter(([, ml]) => ml > 0),
    );
    resetOrder();
    updateOrder({
      orderMode: 'custom',
      customMix: nonZeroMix,
      totalPrice,
      product: {
        id: 'custom',
        icon: '⚗️',
        name: t.customMixProductName,
        desc: '',
        basePrice: totalPrice,
      },
      user,
    });
    navigate('/qr-order');
  };

  const handleBack = () => navigate('/menu-type');

  const pct = Math.min((totalMl / MAX_ML) * 100, 100);
  const isOver = totalMl > MAX_ML;
  const isEmpty = totalMl === 0;

  return (
    <div className="kiosk-app rush-tech-bg relative flex h-full w-full flex-col overflow-hidden [animation:pageFadeIn_0.35s_ease_both]">
      <div className="noise-overlay" />
      <Header title={t.customMixHeading} onBack={handleBack} />

      {loading && (
        <div className="relative z-10 flex flex-1 items-center justify-center">
          <span className="text-[0.9rem] text-[var(--muted)]">Loading ingredients…</span>
        </div>
      )}

      {!loading && error && (
        <div className="relative z-10 flex flex-1 items-center justify-center px-6">
          <p className="text-center text-[0.9rem] text-[var(--muted)]">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="flex flex-1 min-h-0 flex-col gap-3 overflow-y-auto px-4 pt-3 pb-2 sm:grid sm:auto-rows-fr sm:grid-cols-2 sm:content-stretch sm:gap-4 sm:overflow-hidden sm:px-5 sm:pt-4 sm:pb-3">
            {products.map((p, idx) => {
              const ml = mix[p.product_id] ?? 0;
              const cap = capFor(p);
              const sliderPct = cap > 0 ? (ml / cap) * 100 : 0;
              // "+" stays live only while this drink can actually grow — it
              // needs room in the total budget AND room under its own cap
              // (stock and/or max_pour_ml).
              const canAdd = ml > 0
                ? remaining > 0 && ml < cap
                : remaining >= MIN_ML && cap >= MIN_ML;
              const spanFullRow = idx === products.length - 1 && products.length % 2 === 1;
              return (
                <div key={p.product_id} className={`min-h-0 shrink-0 ${spanFullRow ? 'sm:col-span-2' : ''}`}>
                  <DrinkColumn
                    productId={p.product_id}
                    product={p}
                    ml={ml}
                    sliderPct={sliderPct}
                    minMl={MIN_ML}
                    maxMl={cap}
                    stepMl={STEP_ML}
                    mlUnit={t.mlUnit}
                    canAdd={canAdd}
                    onChange={(v) => setAmount(p, v)}
                  />
                </div>
              );
            })}
          </div>

          <div className="kiosk-flow-wrap overflow-visible pb-4 pt-2 sm:pb-5 sm:pt-3">
            <button
              type="button"
              disabled={isEmpty || isOver}
              onPointerUp={handleConfirm}
              className={`touch-target relative w-full overflow-hidden rounded-[18px] border-2 border-[var(--confirm-border)] bg-[var(--confirm-bg)] py-4 font-display text-[clamp(1.1rem,1.7vw,1.55rem)] font-bold uppercase tracking-[0.15em] text-white [text-shadow:0_1px_12px_var(--confirm-text-shadow)] transition-[opacity] duration-150 disabled:cursor-not-allowed disabled:opacity-30 sm:py-6${pct === 100 ? ' confirm-pulse' : ' shadow-[0_0_18px_var(--confirm-shadow)]'}`}
            >
              <div
                className="pointer-events-none absolute inset-y-0 left-0 transition-[width] duration-200"
                style={{
                  width: `${pct}%`,
                  background: isOver
                    ? 'var(--confirm-fill-over)'
                    : pct === 100
                    ? 'var(--confirm-fill-full)'
                    : 'var(--confirm-fill-progress)',
                }}
              />
              <span className="relative confirm-label flex items-center justify-center">
                <span className="flex-1 text-center">{t.confirmMix}</span>
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ── Per-drink column ────────────────────────────────────────────────────────
type DrinkColumnProps = {
  productId: string;
  product: MachineProduct;
  ml: number;
  sliderPct: number;
  minMl: number;
  maxMl: number;
  stepMl: number;
  mlUnit: string;
  canAdd: boolean;
  onChange: (ml: number) => void;
};

const KEYPAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'del', '0', 'ok'];

const NumKeypad = ({ onKey }: { onKey: (k: string) => void }) => (
  <div
    className="fixed inset-x-0 bottom-0 z-50 flex justify-center pb-[clamp(0.75rem,3vh,2rem)]"
    onPointerDown={(e) => e.preventDefault()}
    onPointerUp={(e) => e.stopPropagation()}
  >
    <div className="grid grid-cols-3 gap-2 rounded-[20px] border border-[var(--border)] bg-[var(--card)] p-3 shadow-2xl">
      {KEYPAD_KEYS.map((k) => (
        <button
          key={k}
          type="button"
          onPointerDown={(e) => { e.preventDefault(); onKey(k); }}
          className="touch-target flex h-14 w-20 items-center justify-center rounded-xl bg-[rgba(255,255,255,0.08)] font-display text-2xl font-bold text-white active:bg-[rgba(255,255,255,0.22)] sm:h-16 sm:w-24"
        >
          {k === 'del' ? '⌫' : k === 'ok' ? '✓' : k}
        </button>
      ))}
    </div>
  </div>
);

const DrinkColumn = ({ productId, product, ml, sliderPct, minMl, maxMl, stepMl, mlUnit, canAdd, onChange }: DrinkColumnProps) => {
  const [editing, setEditing] = React.useState(false);
  const [inputVal, setInputVal] = React.useState('');
  const phoneInputRef = React.useRef<HTMLInputElement | null>(null);
  const kioskInputRef = React.useRef<HTMLInputElement | null>(null);

  const name = product.name;

  const startEditing = () => {
    setInputVal(String(ml));
    setEditing(true);
    setTimeout(() => { phoneInputRef.current?.select(); kioskInputRef.current?.select(); }, 0);
  };

  const commitEdit = () => {
    const parsed = parseInt(inputVal, 10);
    if (!isNaN(parsed)) {
      const rounded = Math.round(parsed / stepMl) * stepMl;
      onChange(Math.max(0, Math.min(rounded, maxMl)));
    }
    setEditing(false);
  };

  const bumpUp = () => onChange(ml === 0 ? minMl : ml + stepMl);
  const bumpDown = () => onChange(ml <= minMl ? 0 : ml - stepMl);
  const holdBumpUp = useHoldRepeat(bumpUp);
  const holdBumpDown = useHoldRepeat(bumpDown);

  const handleInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') setEditing(false);
  };

  const handleKeypadKey = (k: string) => {
    if (k === 'ok') { commitEdit(); return; }
    if (k === 'del') { setInputVal((v) => v.slice(0, -1)); return; }
    setInputVal((v) => (v + k).replace(/^0+(?=\d)/, '').slice(0, 4));
  };

  const updateFromPointer = (clientX: number, el: HTMLDivElement) => {
    const rect = el.getBoundingClientRect();
    const fraction = (clientX - rect.left) / rect.width;
    const clamped = Math.max(0, Math.min(1, fraction));
    const value = Math.round((clamped * maxMl) / stepMl) * stepMl;
    onChange(value);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    updateFromPointer(e.clientX, e.currentTarget);
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.buttons === 0 && e.pointerType === 'mouse') return;
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
    updateFromPointer(e.clientX, e.currentTarget);
  };

  const renderReadout = (
    ref: React.RefObject<HTMLInputElement | null>,
    valueClass: string,
    wrapperClass = '',
  ) => (
    <span className={`shrink-0 cursor-text ${wrapperClass}`} onPointerUp={startEditing}>
      {editing ? (
        <>
          <input
            ref={ref}
            type="number"
            inputMode="none"
            min={0}
            max={maxMl}
            step={stepMl}
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleInputKey}
            className={`w-20 border-b border-white bg-transparent text-center font-display font-bold tabular-nums text-white outline-none ${valueClass}`}
          />
          <NumKeypad onKey={handleKeypadKey} />
        </>
      ) : (
        <span className={`font-display font-bold tabular-nums text-white ${valueClass}`}>
          {ml}<span className="ml-0.5 text-[0.75em] font-medium text-[var(--muted)]">{mlUnit}</span>
        </span>
      )}
    </span>
  );

  const renderSlider = (heightClass: string) => (
    <div
      role="slider"
      aria-label={`${name} ml`}
      aria-valuemin={0}
      aria-valuemax={maxMl}
      aria-valuenow={ml}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      className={`relative w-full cursor-pointer overflow-hidden rounded-full bg-[rgba(255,255,255,0.08)] ${heightClass}`}
      style={{ touchAction: 'none' }}
    >
      <div
        className="absolute inset-y-0 left-0 transition-[width] duration-150"
        style={{
          width: `${sliderPct}%`,
          background: ml > 0 ? 'linear-gradient(90deg,rgba(220,220,220,0.9),rgba(255,255,255,1))' : 'transparent',
          boxShadow: ml > 0 ? '0 0 12px rgba(255,255,255,0.35)' : 'none',
        }}
      />
      {ml > 0 && (
        <div
          aria-hidden
          className="pointer-events-none absolute top-1/2 h-[calc(100%+14px)] w-4 -translate-y-1/2 -translate-x-1/2 rounded-full border-2 border-[rgba(255,255,255,0.4)] bg-[#a8a8a8] shadow-[0_2px_8px_rgba(0,0,0,0.6)]"
          style={{ left: `${sliderPct}%` }}
        />
      )}
    </div>
  );

  const renderPlus = (sizeClass: string) => (
    <button
      type="button"
      onPointerDown={holdBumpUp}
      disabled={!canAdd}
      aria-label={`add ${ml === 0 ? minMl : stepMl}ml to ${name}`}
      className={`flex shrink-0 items-center justify-center rounded-lg border border-[var(--accent-border)] bg-[var(--accent-bg)] font-display text-[clamp(1rem,3.2vh,1.8rem)] text-white transition-opacity active:opacity-60 disabled:opacity-25 ${canAdd ? 'shadow-[var(--btn-shadow)]' : ''} ${sizeClass}`}
    >
      +
    </button>
  );

  const renderMinus = (sizeClass: string, withDataId = false) => (
    <button
      type="button"
      onPointerDown={holdBumpDown}
      disabled={ml === 0}
      aria-label={`remove ${stepMl}ml from ${name}`}
      {...(withDataId ? { 'data-product-id': productId } : {})}
      className={`flex shrink-0 items-center justify-center rounded-lg border border-[var(--accent-border)] bg-[var(--accent-bg)] font-display text-[clamp(1rem,3.2vh,1.8rem)] text-white shadow-[var(--btn-shadow)] transition-opacity active:opacity-60 disabled:opacity-25 ${sizeClass}`}
    >
      −
    </button>
  );

  return (
    <>
      {/* Phone: stacked card */}
      <div className="relative flex w-full flex-col overflow-hidden rounded-[22px] border border-[var(--border)] bg-[var(--card)] sm:hidden">
        <DescriptionInfo description={product.description} buttonClassName="right-3 top-3">
          <div className="flex flex-col gap-4 p-4 pr-12">
            <div className="absolute inset-x-0 top-0 h-1 rounded-t-[22px] bg-[var(--accent-bar)]" />
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center [filter:drop-shadow(0_1px_8px_var(--accent-glow))]">
                <DrinkIcon product={product} />
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="truncate font-display text-[1.3rem] font-bold leading-tight text-[var(--cream)]">
                  {name}
                </span>
                <AlcoholBadge product={product} />
              </div>
              {renderReadout(phoneInputRef, 'text-[1.5rem]')}
            </div>
            <div className="flex items-center gap-3.5">
              {renderMinus('h-12 w-12')}
              {renderSlider('h-11')}
              {renderPlus('h-12 w-12')}
            </div>
          </div>
        </DescriptionInfo>
      </div>

      {/* Kiosk: horizontal row */}
      <div className="relative hidden h-full min-h-0 w-full overflow-hidden rounded-[20px] border border-[var(--border)] bg-[var(--card)] sm:block">
        <DescriptionInfo description={product.description} buttonClassName="right-3 top-3">
          <div className="relative flex h-full min-h-0 w-full flex-row items-center gap-[clamp(0.75rem,2vw,2rem)] py-[clamp(0.5rem,1.4vh,1.75rem)] pl-10 pr-14">
            <div className="absolute left-0 top-0 h-full w-1 rounded-l-[20px] bg-[var(--accent-bar)]" />

            <div className="flex h-[clamp(2.5rem,9vh,7rem)] w-[clamp(2.5rem,9vh,7rem)] shrink-0 items-center justify-center [filter:drop-shadow(0_1px_8px_var(--accent-glow))]">
              <DrinkIcon product={product} />
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-[clamp(0.3rem,1.5vh,1.25rem)]">
              <div className="flex items-baseline justify-between gap-3">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="truncate font-display text-[clamp(1.6rem,2.1vw,2rem)] font-bold leading-tight text-[var(--cream)]">
                    {name}
                  </span>
                  <AlcoholBadge product={product} />
                </div>
                {renderReadout(kioskInputRef, 'text-[clamp(1.2rem,1.6vw,1.5rem)]')}
              </div>
              {renderSlider('h-[clamp(1.6rem,5vh,3rem)]')}
            </div>

            <div className="flex shrink-0 flex-col gap-1.5">
              {renderPlus('h-[clamp(2.1rem,6vh,3.5rem)] w-[clamp(2.1rem,6vh,3.5rem)]')}
              {renderMinus('h-[clamp(2.1rem,6vh,3.5rem)] w-[clamp(2.1rem,6vh,3.5rem)]', true)}
            </div>
          </div>
        </DescriptionInfo>
      </div>
    </>
  );
};

export default CustomMixPage;
